import React, { useMemo } from "react";
import { Cell, Pie, PieChart } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  REVENUE_DONUT_CHART_SIZE,
  REVENUE_DONUT_INNER_RADIUS,
  REVENUE_DONUT_OUTER_RADIUS,
  REVENUE_DONUT_SEGMENT_STROKE,
  REVENUE_DONUT_SEGMENT_STROKE_WIDTH,
} from "../revenue/constants";
import { AnalyticsEmptyDonutRing } from "./AnalyticsEmptyDonutRing";
import {
  filterDonutChartSlices,
  hasDonutChartData,
} from "./analyticsDonutUtils";

export type AnalyticsDonutSegment = {
  key: string;
  name: string;
  value: number;
  fill: string;
};

type AnalyticsDonutChartAreaProps = {
  segments: AnalyticsDonutSegment[];
  total: number;
  chartConfig: ChartConfig;
  centerPrimary: React.ReactNode;
  centerSecondary?: React.ReactNode;
  pieAnimationKey?: string;
  animate?: boolean;
  tooltip?: (props: Record<string, unknown>) => React.ReactNode;
  /** When set, overrides the default total-based empty-state check. */
  hasData?: boolean;
};

export const AnalyticsDonutChartArea: React.FC<
  AnalyticsDonutChartAreaProps
> = ({
  segments,
  total,
  chartConfig,
  centerPrimary,
  centerSecondary,
  pieAnimationKey,
  animate = false,
  tooltip,
  hasData,
}) => {
  const chartSlices = useMemo(
    () => filterDonutChartSlices(segments),
    [segments],
  );
  const showChart = hasData ?? hasDonutChartData(total, segments);

  return (
    <div
      className="relative shrink-0"
      style={{
        width: REVENUE_DONUT_CHART_SIZE,
        height: REVENUE_DONUT_CHART_SIZE,
      }}
    >
      {showChart ? (
        <ChartContainer config={chartConfig} className="!aspect-auto size-full">
          <PieChart key={pieAnimationKey}>
            {tooltip ? <ChartTooltip content={tooltip} /> : null}
            <Pie
              data={chartSlices}
              dataKey="value"
              nameKey="name"
              innerRadius={REVENUE_DONUT_INNER_RADIUS}
              outerRadius={REVENUE_DONUT_OUTER_RADIUS}
              strokeWidth={REVENUE_DONUT_SEGMENT_STROKE_WIDTH}
              stroke={REVENUE_DONUT_SEGMENT_STROKE}
              isAnimationActive={animate}
              animationBegin={animate ? 0 : undefined}
              animationDuration={animate ? 500 : undefined}
              animationEasing={animate ? "ease-out" : undefined}
            >
              {chartSlices.map((segment) => (
                <Cell key={segment.key} fill={segment.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      ) : (
        <AnalyticsEmptyDonutRing />
      )}

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-lg font-semibold text-[#171717]">
          {centerPrimary}
        </span>
        {centerSecondary ? (
          <span className="text-xs text-muted-foreground">
            {centerSecondary}
          </span>
        ) : null}
      </div>
    </div>
  );
};
